import React, { FC, useContext, useState, useEffect } from 'react';
import {
  createStyles,
  makeStyles,
  Theme,
  Typography,
  Box,
  Popover,
  MenuItem,
  Button,
  IconButton,
} from '@material-ui/core';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import { Close, ExpandMore } from '@material-ui/icons';
import colors from '../../../../themes/main-colors';
import SlugLink from '../../../slug-link';
import { LanguageContext } from '../../../../providers/LanguageProvider';
import { useIsMobile } from '../../../../util/style-utils';
import { useTranslation } from '../../../../hooks/useTranslation';
import ROUTES from '../../../../routes';
import { useLocation } from '@reach/router';

const categoriesQuery = graphql`
  query allArticleCategoryQuery {
    allContentfulCategory(
      filter: { slug: { ne: null, regex: "/^((?!placeholder).)*$/i" } }
      sort: { fields: categoryOrder, order: ASC }
    ) {
      nodes {
        ...BlogCategory
      }
    }
  }
`;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    categoryWrapper: {
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'center',
      height: 84,
      [theme.breakpoints.down('sm')]: {
        border: 'none',
        width: '100%',
        marginTop: 0,
        marginBottom: 0,
        height: 'auto',
        borderBottom: `1px solid ${colors.grey3}`,
        borderRadius: 0,
        marginRight: 0,
        marginLeft: 0,
      },
    },
    formControl: {
      width: '100%',
      '& .MuiSelect-selectMenu > p': {
        color: colors.darkGray3,
      },
    },
    categoryCard: {
      width: 'max-content',
      textAlign: 'center',
      padding: 15,
      cursor: 'pointer',
      textDecoration: 'none',
      '& a': {
        margin: 0,
        fontSize: '1.25rem',
        whiteSpace: 'nowrap',
        textDecoration: 'none',
        color: colors.noirBlur,
        fontWeight: 400,
        textTransform: 'uppercase',
        borderBottom: `2px solid ${'transparent'}`,
        '&:hover': {
          borderBottom: `2px solid ${colors.salmon}`,
        },
        '&.currentLink': {
          fontWeight: 700,
          color: colors.noirBlur,
          borderBottom: `2px solid ${colors.salmon}`,
        },
      },
    },
    listItemText: {
      color: colors.noirBlur,
      textTransform: 'uppercase',
    },
    selectedItem: {
      fontFamily: 'Untitled-Sans',
      fontSize: theme.spacing(2.25),
      lineHeight: theme.typography.pxToRem(22.48),
      color: colors.noirBlur,
    },
    dropDownButton: {
      width: '100%',
      padding: '20px',
      color: colors.darkGray3,
      background: colors.white,
      boxShadow: 'none',
      height: 60,
      justifyContent: 'space-between',
      '&:hover': {
        boxShadow: 'none',
        background: 'transparent',
      },
    },
    popOverStyles: {
      '& > .MuiPopover-paper': {
        width: '100%',
        left: '0 !important',
        maxWidth: '100%',
        boxShadow: 'none',
        marginTop: -2,
      },
    },
    iconButton: {
      color: colors.noirBlur,
    },
  }),
);

type Category = {
  name: string;
  path: string;
};

type Props = {
  currentArticleCategoryName?: string;
};

const Categories: FC<Props> = (props: Props) => {
  const { currentArticleCategoryName } = props;
  const { language } = useContext(LanguageContext);
  const isMobile = useIsMobile();
  const classes = useStyles();
  const { t } = useTranslation();
  const location = useLocation();
  const { allContentfulCategory } = useStaticQuery(categoriesQuery);

  const blogHomeCategory = {
    name: t('BLOG_HOME'),
    path: ROUTES.BLOG_HOME,
  };

  const [categories, setCategories] = useState<Category[] | undefined | null>(
    [],
  );
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null,
  );
  const [mobileNavAnchorEl, setMobileNavAnchorEl] = useState(null);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const getCategoryPathFromPath = (): string | undefined => {
    const currentPath = location.pathname.replace(/\/$/, ''); //Strip trailing slash.

    if (currentPath === blogHomeCategory.path) {
      setSelectedCategory(blogHomeCategory);
      return undefined;
    }

    if (
      currentPath.includes(`${blogHomeCategory.path}/topic/`) ||
      currentPath.includes(`${blogHomeCategory.path}/author/`)
    ) {
      return undefined;
    }

    return currentPath.slice(ROUTES.BLOG_CATEGORY.length);
  };

  useEffect(() => {
    const categoryPath = getCategoryPathFromPath();

    const categoriesByLocale = allContentfulCategory?.nodes.filter(
      (node: any) => node.node_locale === language,
    );

    const cmsCatergories: Category[] = categoriesByLocale?.map(
      (cmsCategory: any) => {
        const category = {
          name: cmsCategory.name,
          path: cmsCategory.slug.replace(/^\/|\/$/g, ''),
        };

        if (
          (categoryPath && categoryPath === category.path) ||
          currentArticleCategoryName === category.name
        ) {
          setSelectedCategory(category);
        }

        return category;
      },
    );

    setCategories([blogHomeCategory, ...cmsCatergories]);
  }, []);

  const handleOpenMobileNavMenuClick = (event: any) => {
    setMobileNavAnchorEl(event.currentTarget);
    setIsMobileNavOpen(true);
  };

  const handleCloseMobileNavMenuClick = () => {
    setMobileNavAnchorEl(null);
    setIsMobileNavOpen(false);
  };

  const handleMobileNavClick = async (categoryPath: string) => {
    const route =
      categoryPath === blogHomeCategory.path
        ? categoryPath
        : ROUTES.BLOG_CATEGORY + categoryPath;
    await navigate(route);
    setIsMobileNavOpen(false);
  };

  return (
    <Box className={classes.categoryWrapper}>
      {!isMobile ? (
        categories?.map(({ name, path }: Category, index: number) => {
          return (
            <div className={classes.categoryCard} key={index}>
              <SlugLink
                slug={path}
                linkText={name}
                className={name === selectedCategory?.name ? 'currentLink' : ''}
              />
            </div>
          );
        })
      ) : (
        <>
          <Button
            variant="contained"
            onClick={handleOpenMobileNavMenuClick}
            className={classes.dropDownButton}
            disableRipple
            data-testid={'mobile-nav-button'}
          >
            <Typography variant={'h1'} className={classes.selectedItem}>
              {selectedCategory?.name
                ? `${selectedCategory?.name} ${t('POSTS')}`
                : t('RELATED_POSTS')}
            </Typography>
            <IconButton>
              {isMobileNavOpen ? (
                <Close className={classes.iconButton} />
              ) : (
                <ExpandMore className={classes.iconButton} />
              )}
            </IconButton>
          </Button>
          <Popover
            open={isMobileNavOpen}
            anchorEl={mobileNavAnchorEl}
            onClose={handleCloseMobileNavMenuClick}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            className={classes.popOverStyles}
            data-testid={'mobile-nav-menu'}
          >
            {categories?.map(({ name, path }: Category, index: number) => {
              return (
                <MenuItem
                  key={`nav-${index}`}
                  value={name}
                  onClick={async (e: any) => handleMobileNavClick(path)}
                >
                  <Typography
                    variant="body1"
                    className={classes.listItemText}
                    data-testid={`nav-${index}`}
                  >
                    {name}
                  </Typography>
                </MenuItem>
              );
            })}
          </Popover>
        </>
      )}
    </Box>
  );
};

export default Categories;
